import React from 'react';
import { t } from '@lingui/macro';

import { Box, Button, Flex, Text } from '@rover/kibble/core';
import type { Props as ButtonProps } from '@rover/kibble/core/Button';
import useTheme from '@rover/kibble/themes/useTheme';
import { isReactNative } from '@rover/rsdk/src/modules/Network/userAgent';
import type { RoverTheme } from '@rover/types/src/RoverTheme';

import { SpacingSystemType } from '../../core/styledSystemPropTypes';
import { HybridOnPressHandler } from '../../core/utils';

import type { ChipRole, ChipSize, ChipType } from '.';

type ChipProps = {
  id: string;
  label: string;
  value: string | number;
  count?: number; // Optional count for unread messages or similar
  icon?: React.ComponentType;
  isCompact?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  selectedIcon?: React.ComponentType;
  role?: ChipRole;
  size?: ChipSize;
  /** Don't set tabIndex manually; let the chip group components handle it */
  tabIndex?: number;
  chipRef?: React.RefObject<ChipType>;
  onClick?: HybridOnPressHandler;
  accessibilityHint?: string;
  iconGap?: SpacingSystemType;
  paddingX?: SpacingSystemType | [SpacingSystemType, SpacingSystemType];
  paddingY?: SpacingSystemType | [SpacingSystemType, SpacingSystemType];
  fontWeight?: 'regular' | 'semibold';
};

export type OptionProps = Pick<
  ChipProps,
  'id' | 'label' | 'value' | 'count' | 'icon' | 'isDisabled'
>;

const CountBubble = ({ count, theme }: { count: number; theme: RoverTheme }): JSX.Element => (
  <Box
    webAs="span"
    // @ts-expect-error - We need to change the types for Box to allow these props; see DEV-130883
    m={theme.space['0x']}
    // @ts-expect-error - We need to change the types for Box to allow these props; see DEV-130883
    mr={theme.space['2x']}
    // @ts-expect-error - We need to change the types for Box to allow these props; see DEV-130883
    px={theme.space['1x']}
    borderRadius={theme.radii.round}
    // @ts-expect-error - We need to change the types for Box to allow these props; see DEV-130883
    background={theme.background.interactive.destructive}
    sx={{ lineHeight: isReactNative() ? undefined : 0 }}
  >
    <Text
      {...(isReactNative() ? {} : { as: 'span' })}
      size="50"
      fontWeight="semibold"
      textColor="primary_inverse"
      aria-label={t`${count} unread`}
      data-testid="kibble-patterns-chip-unread-count"
    >
      {count}
    </Text>
  </Box>
);

const Chip: React.FC<ChipProps> = ({
  id,
  label,
  count,
  icon,
  isCompact = false,
  isDisabled = false,
  isSelected,
  selectedIcon,
  role,
  size = 'default',
  tabIndex = 0,
  chipRef,
  onClick,
  accessibilityHint,
  iconGap,
  paddingX,
  paddingY,
  fontWeight = 'semibold',
}) => {
  const theme = useTheme();

  const textSize = size === 'small' ? '100' : '200';
  const textColor = 'secondary';
  const shouldAnnounceAsChecked = role === 'checkbox' || role === 'radio';

  const webProps = {
    role,
    tabIndex,
    'aria-checked': shouldAnnounceAsChecked ? isSelected : undefined,
    'aria-pressed': !shouldAnnounceAsChecked ? isSelected : undefined, // VoiceOver only respects aria-pressed for buttons, but reads it as "selected"
    'aria-selected': !shouldAnnounceAsChecked ? isSelected : undefined,
    'aria-disabled': isDisabled,
    'data-testid': 'kibble-patterns-chip',
    childrenFlexGap: iconGap as string,
  };

  const rxnProps = {
    rxnRef: chipRef as unknown as React.RefObject<typeof Box>,
    accessibilityHint,
    accessibilityRole: role,
    accessibilityState: {
      checked: shouldAnnounceAsChecked ? isSelected : undefined,
      selected: !shouldAnnounceAsChecked ? isSelected : undefined,
      disabled: isDisabled,
    },
    testID: 'kibble-patterns-chip',
  };

  const buttonStyles: ButtonProps['sx'] = {
    borderWidth: theme.borderWidth.primary,
    borderStyle: 'solid',
    borderRadius: theme.radii.primary,
    outlineOffset: '4px',
  };
  const disabledButtonStyles: ButtonProps['sx'] = {
    background: theme.background.interactive.disabled,
    borderColor: theme.colors.interactive_border.disabled,
    textColor: theme.textColor.disabled,
    cursor: 'not-allowed',
  };

  if (size !== 'small' && isCompact) {
    buttonStyles.py = theme.space['2x'];
    buttonStyles.px = theme.space['3x'];
  }

  // Apply custom padding if provided
  if (paddingX) {
    buttonStyles.px = paddingX;
  }
  if (paddingY) {
    buttonStyles.py = paddingY;
  }

  return (
    <Button
      key={id}
      {...(isReactNative() ? { rxnProps } : webProps)}
      icon={icon}
      iconAlignment="right"
      selectedIcon={selectedIcon}
      isSelected={isSelected}
      size={size}
      onPress={isDisabled ? () => {} : onClick}
      sx={{
        ...buttonStyles,
        ...(isDisabled
          ? disabledButtonStyles
          : {
              background: isSelected
                ? theme.background.interactive.button_primary_inverse_base
                : theme.background.interactive.primary_base,
              borderColor: isSelected
                ? theme.colors.interactive_text.primary_active
                : theme.colors.border.primary,
              textColor: isSelected ? theme.textColor.primary : theme.textColor.tertiary,
              '&:hover, &:active': {
                background: isSelected
                  ? theme.background.interactive.button_primary_inverse_pressed
                  : theme.background.interactive.primary_pressed,
                borderColor: isSelected
                  ? theme.colors.interactive_text.primary_active
                  : theme.colors.interactive_border.primary_base,
              },
            }),
      }}
    >
      <Flex flexDirection="row" alignItems="center" justifyContent="flex-end">
        {count !== undefined && <CountBubble count={count} theme={theme} />}

        <Text
          {...(isReactNative() ? {} : { as: 'span' })}
          m="0x"
          fontWeight={fontWeight}
          size={textSize}
          textColor={textColor}
        >
          {label}
        </Text>
      </Flex>
    </Button>
  );
};

export default Chip;
